import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { TitleBar } from '@resistapp/client/components/bars/title-bar';
import { theme } from '@resistapp/client/components/shared/theme';
import { MaybeApiError } from '@resistapp/client/components/states/error';
import { LoadingView } from '@resistapp/client/components/states/loading';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import {
  useAdminShipment,
  useCountries,
  useDeleteAdminShipment,
  usePatchAdminShipment,
} from '@resistapp/client/hooks/api';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { isResistomapAdminUser } from '@resistapp/client/utils/access';
import { useQueryClient } from '@tanstack/react-query';
import { omit } from 'lodash';
import { useNavigateWithQuery } from '@resistapp/client/hooks/use-navigate-with-query';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShipmentDocumentAdminCentre } from '../shipment-view/shipment-document-centre';
import { ShipmentForm, buildShipmentFormDefaults } from './shipment-form';

export function AdminShipmentReadView() {
  const params = useParams();
  const shipmentId = params.id ? Number(params.id) : undefined;
  const navigate = useNavigateWithQuery();
  const queryClient = useQueryClient();
  const showToast = useToastWithoutDebounce();
  const { user } = useUser();
  const isAdmin = isResistomapAdminUser(user);

  const [isEditing, setIsEditing] = useState(false);
  const [formResetKey, setFormResetKey] = useState(0);

  const { data: shipment, isPending, error } = useAdminShipment(shipmentId);
  const { data: countriesByAlpha3 } = useCountries();
  const patchShipment = usePatchAdminShipment();
  const deleteShipment = useDeleteAdminShipment();

  const defaultValues = useMemo(() => {
    if (!shipment) {
      return null;
    }

    return buildShipmentFormDefaults({
      organisationId: shipment.organisationId,
      labWorkbookId: shipment.labWorkbookId,
      status: shipment.status,
      pickupDate: shipment.pickupDate,
      pickupWindowStart: shipment.pickupWindowStart,
      pickupWindowEnd: shipment.pickupWindowEnd,
      contactName: shipment.contactName,
      contactEmail: shipment.contactEmail,
      contactTelephoneNumber: shipment.contactTelephoneNumber,
      pickupCountry: shipment.pickupCountry,
      pickupAddressLine1: shipment.pickupAddressLine1,
      pickupAddressLine2: shipment.pickupAddressLine2,
      pickupAddressLine3: shipment.pickupAddressLine3,
      pickupPostalCode: shipment.pickupPostalCode,
      pickupCity: shipment.pickupCity,
      taxId: shipment.taxId,
      packageWeightKg: shipment.packageWeightKg,
      packageLengthCm: shipment.packageLengthCm,
      packageWidthCm: shipment.packageWidthCm,
      packageHeightCm: shipment.packageHeightCm,
      items: shipment.items.map(item => ({
        description: item.description,
        count: item.count,
      })),
    });
  }, [shipment]);

  if (isPending) {
    return <LoadingView />;
  }

  if (error) {
    return (
      <Box padding={theme.spacing[6]}>
        <MaybeApiError error={error} />
      </Box>
    );
  }

  if (!shipmentId) {
    return (
      <Box padding={theme.spacing[6]}>
        <Text>Shipment not found.</Text>
      </Box>
    );
  }

  const handleCancel = () => {
    setIsEditing(false);
    setFormResetKey(prev => prev + 1);
  };

  const handleDelete = () => {
    void (async () => {
      const confirmed = window.confirm(`Delete shipment #${shipmentId}?`);
      if (!confirmed) {
        return;
      }
      try {
        await deleteShipment.mutateAsync({ shipmentId });
        await queryClient.invalidateQueries({
          predicate: query => typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith('/admin/shipments'),
        });
        showToast({
          title: 'Shipment deleted',
          description: `Shipment #${shipmentId} was deleted.`,
          status: 'success',
          duration: 5000,
        });
        navigate('/admin/shipments');
      } catch (deleteError) {
        showToast({
          title: 'Failed to delete shipment',
          description: deleteError instanceof Error ? deleteError.message : 'Please try again later.',
          status: 'error',
          duration: 7000,
        });
      }
    })();
  };

  return (
    <div style={{ marginBottom: theme.spacing[16] }}>
      <TitleBar title={`Shipment #${shipmentId}`} $extraWide={true} />
      <VStack align="stretch" spacing={4} padding={theme.spacing[4]}>
        <ShipmentForm
          key={formResetKey}
          mode="edit"
          readOnly={!isEditing}
          copyReadOnlyValues={!isEditing && isAdmin}
          defaultValues={defaultValues as ReturnType<typeof buildShipmentFormDefaults>}
          countriesByAlpha3={countriesByAlpha3}
          organisationOptions={[{ id: shipment.organisation.id, name: shipment.organisation.name }]}
          labWorkbookOptions={
            shipment.labWorkbook
              ? [{ id: shipment.labWorkbook.id, name: shipment.labWorkbook.name ?? 'Untitled workbook' }]
              : undefined
          }
          disableOrganisationSelect={true}
          disableLabWorkbookSelect={true}
          allowStatusEdit={true}
          submitLabel="Save changes"
          isSubmitting={patchShipment.isPending}
          extraButtons={
            isEditing ? (
              <>
                <Button colorScheme="red" variant="outline" isLoading={deleteShipment.isPending} onClick={handleDelete}>
                  Delete
                </Button>
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
              </>
            ) : undefined
          }
          onSubmit={async values => {
            const patchBody = omit(values, ['organisationId', 'labWorkbookId']);
            await patchShipment.mutateAsync({
              shipmentId,
              body: patchBody,
            });
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: [`/admin/shipment/${shipmentId}`] }),
              queryClient.invalidateQueries({
                predicate: query =>
                  typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith('/admin/shipments'),
              }),
              queryClient.invalidateQueries({
                predicate: query =>
                  typeof query.queryKey[0] === 'string' &&
                  query.queryKey[0].startsWith(`/admin/organisations/${shipment.organisation.id}/shipments`),
              }),
              queryClient.invalidateQueries({ queryKey: ['/shipping/workbooks'] }),
            ]);
            showToast({
              title: 'Shipment saved',
              description: `Shipment #${shipmentId} was updated.`,
              status: 'success',
              duration: 7000,
            });
            setIsEditing(false);
          }}
        />
        <MaybeApiError error={patchShipment.error} />
        {!isEditing && isAdmin ? (
          <HStack>
            <Button
              colorScheme="blue"
              onClick={() => {
                setIsEditing(true);
              }}
            >
              Edit
            </Button>
          </HStack>
        ) : null}
        {isAdmin ? <ShipmentDocumentAdminCentre key={shipment.id} shipment={shipment} /> : null}
      </VStack>
    </div>
  );
}
